import _ from 'lodash';
import { rest } from '@ax2/common/index';
import PanelType from '@ax2/common/model/PanelType';
import UUID from '@ax2/common/model/UUID';

const SHOP_PENDING_PURCHASE_ID_KEY = 'shop-pending-purchase-id';
const SHOP_ORDER_KEY = 'shop-order-v2';

const API_URL = '/payment/v1';

export interface OrderItem {
  panelType: PanelType;
  specialty?: number;
  quantity: number;
}

export type Order = OrderItem[];

const getProductName = (type: PanelType) => {
  switch (type) {
    case PanelType.HealthPlanAndPbm:
      return 'Health plan + pharmacy benefit manager (PBM) survey';
    case PanelType.OncologyCenter:
      return 'Oncology center survey';
    case PanelType.HealthPlan:
      return 'Health plan survey';
    case PanelType.HospitalNetwork:
      return 'Hospital system survey';
    case PanelType.HealthPlanAndHospitalNetwork:
      return 'Health plan + hospital system survey';
    case PanelType.PhysicianSurvey:
      return 'Physician survey';
    default:
      throw Error(`Unknown PanelType (${type})`);
  }
};

export interface ProductDescription {
  name: string;
  price: number;
  type?: string;
}

export const products: {
  [itemId in PanelType]: ProductDescription;
} = {
  HealthPlanAndPbm: {
    name: getProductName(PanelType.HealthPlanAndPbm),
    price: 10000,
  },
  OncologyCenter: {
    name: getProductName(PanelType.OncologyCenter),
    price: 10000,
  },
  HealthPlan: {
    name: getProductName(PanelType.HealthPlan),
    price: 10000,
  },
  HospitalNetwork: {
    name: getProductName(PanelType.HospitalNetwork),
    price: 10000,
  },
  HealthPlanAndHospitalNetwork: {
    name: getProductName(PanelType.HealthPlanAndHospitalNetwork),
    price: 10000,
  },
  PhysicianSurvey: {
    name: getProductName(PanelType.PhysicianSurvey),
    price: 20000,
    type: PanelType.PhysicianSurvey,
  },
};

export function getClientToken(): Promise<string> {
  const url = API_URL + '/token';
  return rest.api<string>(url, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  });
}

export function countTotal(order: Order): number {
  return _.sum(
    order.map(i => {
      const q = i.quantity;
      const price = i.panelType
        ? products[i.panelType as PanelType].price || 0
        : 0;
      return q * price;
    })
  );
}

interface ApiOrderPanelItem {
  name: string;
  specialty?: number;
}
interface ApiOrder {
  panels: ApiOrderPanelItem[];
  couponCode?: string;
}

function orderToApi(order: Order, coupon?: string): ApiOrder {
  return {
    couponCode: coupon,
    panels: order
      .filter(i => !!i.panelType)
      .map(i => {
        return {
          // eslint-disable-next-line  @typescript-eslint/no-non-null-assertion
          name: i.panelType!,
          specialty: i.specialty,
          quantity: i.quantity,
        };
      }),
  };
}

/**
 * Save an order temporarily, without using the API.
 */
export function saveOrder(order: Order) {
  sessionStorage.setItem(SHOP_ORDER_KEY, JSON.stringify(order));
}

/**
 * Get a temporarily saved order.
 */
export function getOrder(): Order | null {
  const jsonValue = sessionStorage.getItem(SHOP_ORDER_KEY);
  if (jsonValue) {
    return JSON.parse(jsonValue);
  }
  return null;
}

export function saveIcon(name: string, color: string) {
  sessionStorage.setItem(name, JSON.stringify(color));
}

export function getIcon(name: string): string {
  const jsonValue = sessionStorage.getItem(name);
  if (jsonValue) {
    return JSON.parse(jsonValue);
  }
  return 'false';
}

export function saveQuotas(surveyId: UUID, Panel: string, value: number) {
  const key = `${surveyId}_${Panel}`;
  sessionStorage.setItem(key, JSON.stringify(value));
}

export function getQuotas(
  surveyId: UUID,
  Panel: string,
  audience: string
): number {
  const key = `${surveyId}_${audience}`;
  const jsonValue = sessionStorage.getItem(key);
  if (jsonValue) {
    return JSON.parse(jsonValue);
  } else if (Panel == 'HealthPlanAndPbm') {
    if (audience == 'Health Plan') return 18;
    else if (audience == 'PBM') return 3;
  }
  return 10;
}

export function saveQuotasStatus(surveyId: UUID, value: boolean) {
  sessionStorage.setItem(surveyId, JSON.stringify(value));
}

export function getQuotasStatus(surveyId: UUID): boolean {
  const jsonValue = sessionStorage.getItem(surveyId);
  if (jsonValue) {
    return JSON.parse(jsonValue);
  }
  return false;
}

/**
 * Create a new order via API call.
 * @return A promise of order ID (string)
 */
export function createPendingPurchase(
  order: Order,
  coupon?: string
): Promise<string> {
  const url = API_URL + '/survey-order/create';
  return rest
    .api<string>(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      data: JSON.stringify(orderToApi(order, coupon)),
    })
    .then(id => {
      sessionStorage.setItem(SHOP_PENDING_PURCHASE_ID_KEY, id);
      return id;
    });
}

/**
 * Get an ID of a previously placed order.
 */
export function getPendingPurchaseId(): string | null {
  return sessionStorage.getItem(SHOP_PENDING_PURCHASE_ID_KEY);
}

/**
 * Pay for a previously placed order by its ID.
 */
export function payPurchase(orderId: string, nonce?: string): Promise<string> {
  const url = API_URL + '/survey-order/pay';
  return rest
    .api<string>(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      data: JSON.stringify({
        surveyOrderId: orderId,
        nonce: nonce,
      }),
    })
    .then(result => {
      sessionStorage.removeItem(SHOP_ORDER_KEY);
      sessionStorage.removeItem(SHOP_PENDING_PURCHASE_ID_KEY);
      return result;
    });
}

export interface TotalResult {
  itemsAmount: number; // before subtracting the discount
  discount: number;
  totalAmount: number;
}

export function getTotal(order: Order, coupon?: string): Promise<TotalResult> {
  if (coupon) {
    const url = API_URL + '/coupon/validate';
    return rest.api<TotalResult>(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      data: JSON.stringify(orderToApi(order, coupon)),
    });
  } else {
    return new Promise<TotalResult>(resolve =>
      resolve({
        itemsAmount: countTotal(order),
        discount: 0,
        totalAmount: countTotal(order),
      })
    );
  }
}
